import {
  getCurrentOrgId,
  waitForOrgContext,
  getToken as getTokenFromContext,
  setupOrganizationInterceptor,
} from "../../../../shared/orgContext";
import type {
  EventPendingItem,
  InvitationPendingItem,
  NamePendingItem,
  QueueItem,
  SplitCandidateItem,
  TagsPendingItem,
} from "./types";

const API_BASE = "/api";

let orgFetch: typeof fetch = fetch;

function getToken(): string | null {
  return (
    getTokenFromContext() ||
    (window as unknown as Record<string, string>).__HAVEN_TOKEN__ ||
    null
  );
}

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  let orgId = getCurrentOrgId();
  if (!orgId) {
    orgId = await waitForOrgContext(5000);
    if (orgId) orgFetch = setupOrganizationInterceptor(orgId);
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await orgFetch(`${API_BASE}${path}`, { ...options, headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(
      (body as { error?: string }).error || `API error: ${res.status}`,
    );
  }
  return res.json() as Promise<T>;
}

interface DocumentRow {
  id: string;
  original_name: string;
  status: string;
  ai_suggested_name: string | null;
  created_at: string;
  mime_type: string;
  size_bytes: number;
  tags: { id: string; name: string; color: string | null }[];
  metadata: {
    suggested_tags?: string[];
    proposed_tags?: string[];
    suggested_tags_reasoning?: string;
    split_suggestion?: {
      confidence: "high" | "medium" | "low";
      proposedParts: { pages: string; title?: string }[];
      reasoning: string | null;
      pageCount: number;
    };
  } | null;
}

/** Shape of one row from GET /api/invitations/pending-for-me. Matches
 *  the server's snake_case-vs-camelCase mixed conventions on that
 *  route (org_id / role are snake, orgName / invitedByName are camel
 *  in the service return — the route response just mirrors whatever
 *  the service returns). */
interface InvitationPendingRow {
  id: string;
  token: string;
  email: string;
  orgId: string;
  orgName: string;
  role: "OWNER" | "ADMIN" | "MEMBER";
  invitedBy: string;
  invitedByName: string;
  expiresAt: string;
  createdAt: string;
}

/** Shape of one row from GET /api/document-events. `document_mime_type`
 *  and `document_size_bytes` are joined from the parent documents row so
 *  the queue's View button has enough to open the shared preview modal
 *  without a second fetch. */
interface DocumentEventRow {
  id: string;
  document_id: string;
  document_original_name: string;
  document_mime_type: string;
  document_size_bytes: number;
  title: string;
  description: string | null;
  location: string | null;
  start_time: string;
  end_time: string | null;
  all_day: boolean;
  status: string;
  created_at: string;
  updated_at: string;
}

/**
 * Fetch a document as a Blob for the shared preview modal. Mirrors the
 * helpers in the doc-manager and upload-inbox modules — each surface
 * owns its own api client so the queue widget doesn't cross-import.
 */
export async function fetchDocumentBlob(documentId: string): Promise<Blob> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await orgFetch(
    `${API_BASE}/documents/${documentId}/download?inline=true`,
    { headers },
  );
  if (!res.ok) throw new Error(`Preview failed: ${res.status}`);
  return res.blob();
}

/**
 * Fetch server-rendered HTML preview for docx / csv. Mirrors the
 * helpers in the doc-manager and upload-inbox api modules.
 */
export async function fetchDocumentPreviewHtml(
  documentId: string,
): Promise<string> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await orgFetch(
    `${API_BASE}/documents/${documentId}/preview`,
    { headers },
  );
  if (!res.ok) throw new Error(`Preview render failed: ${res.status}`);
  return res.text();
}

/**
 * Download a document (blob + filename). Wired to the preview modal's
 * Download button so a reviewer can grab the file straight from the
 * viewer they've already opened.
 */
export async function downloadDocument(
  documentId: string,
): Promise<{ blob: Blob; filename: string }> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await orgFetch(`${API_BASE}/documents/${documentId}/download`, {
    headers,
  });
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);

  const cd = res.headers.get("Content-Disposition") || "";
  const match = cd.match(/filename="?(.+?)"?$/);
  const filename = match ? decodeURIComponent(match[1]) : "document";

  return { blob: await res.blob(), filename };
}

/**
 * Fetch every open queue item for the active org. A single doc can produce
 * multiple items (name + tags + split + event), which is why we fan out
 * per action type rather than mapping 1-to-1 from docs.
 *
 * Fans two calls in parallel — pending documents (source of name / tags /
 * split items) and pending document_events. Both are backed by the
 * ?status=pending_review filter so an accepted/rejected item disappears
 * on the next refetch with no other work.
 */
export interface QueueFeatureFlags {
  ai_tagging: boolean;
  event_detection: boolean;
  ai_split_suggestions: boolean;
}

/** Tester feature flags decide which action kinds appear. The API 404s
 *  /document-events when event_detection is off, so skip the fetch rather
 *  than fail the whole queue. */
export async function listQueueItems(
  features: QueueFeatureFlags,
): Promise<QueueItem[]> {
  const [docsRes, eventsRes, invitesRes] = await Promise.all([
    apiFetch<{ documents: DocumentRow[] }>(
      "/documents?status=pending_review",
    ),
    features.event_detection
      ? apiFetch<{ events: DocumentEventRow[] }>(
          "/document-events?status=pending_review",
        )
      : Promise.resolve({ events: [] as DocumentEventRow[] }),
    // Path-2 org invitations for the currently signed-in user. Route is
    // outside org context (an invitation is inherently cross-org), so
    // the tag/name/event calls above and this one all coexist without
    // any orgId gymnastics.
    apiFetch<{ invitations: InvitationPendingRow[] }>(
      "/invitations/pending-for-me",
    ).catch(() => ({ invitations: [] as InvitationPendingRow[] })),
  ]);
  const documents = docsRes.documents;
  const events = eventsRes.events;
  const invitations = invitesRes.invitations;

  const items: QueueItem[] = [];

  for (const d of documents) {
    // name_pending — every pending_review doc has an unresolved name decision.
    const namePending: NamePendingItem = {
      type: "name_pending",
      id: `name:${d.id}`,
      createdAt: d.created_at,
      documentId: d.id,
      originalName: d.original_name,
      mimeType: d.mime_type,
      sizeBytes: d.size_bytes,
      suggestedName: d.ai_suggested_name || d.original_name,
    };
    items.push(namePending);

    // tags_pending — only if there's actual unresolved tag work. Filter
    // suggested_tags against already-applied tags (case-insensitive) the
    // same way Upload Inbox does, so a tag the user already accepted from
    // Upload Inbox doesn't reappear here as an outstanding decision.
    const appliedLower = new Set(
      (d.tags || []).map((t) => t.name.toLowerCase()),
    );
    const suggestedTags = (d.metadata?.suggested_tags ?? []).filter(
      (name) => !appliedLower.has(name.toLowerCase()),
    );
    const proposedTags = d.metadata?.proposed_tags ?? [];

    if (features.ai_tagging && suggestedTags.length + proposedTags.length > 0) {
      const tagsPending: TagsPendingItem = {
        type: "tags_pending",
        id: `tags:${d.id}`,
        createdAt: d.created_at,
        documentId: d.id,
        originalName: d.original_name,
        mimeType: d.mime_type,
        sizeBytes: d.size_bytes,
        suggestedTags,
        proposedTags,
        suggestionReasoning: d.metadata?.suggested_tags_reasoning ?? null,
      };
      items.push(tagsPending);
    }

    // split_candidate — the worker only writes split_suggestion when
    // confidence is medium/high and coverage validates, so we don't
    // re-check that here. Belt-and-braces: still guard against a stray
    // low-confidence blob if the worker's contract ever slips.
    const split = d.metadata?.split_suggestion;
    if (
      features.ai_split_suggestions &&
      split &&
      Array.isArray(split.proposedParts) &&
      split.proposedParts.length >= 2 &&
      (split.confidence === "high" || split.confidence === "medium")
    ) {
      const splitItem: SplitCandidateItem = {
        type: "split_candidate",
        id: `split:${d.id}`,
        createdAt: d.created_at,
        documentId: d.id,
        originalName: d.original_name,
        mimeType: d.mime_type,
        sizeBytes: d.size_bytes,
        confidence: split.confidence,
        proposedParts: split.proposedParts,
        reasoning: split.reasoning ?? null,
        pageCount: split.pageCount,
        documentMetadata: (d.metadata ?? {}) as Record<string, unknown>,
      };
      items.push(splitItem);
    }
  }

  // event_pending — the /document-events fetch already filtered to
  // status=pending_review, so a straight map is enough. Storage lives in
  // a separate table (document_events), not documents.metadata, so
  // there's no dependency on the doc row above being present.
  for (const evRow of events) {
    items.push(eventPendingFromRow(evRow));
  }

  // invitation_pending — cross-org action; not tied to any document
  // in the caller's active org. Bulk-accept explicitly excludes these
  // in QueueWidget because accepting an invitation switches active org
  // and doing that 3× per click is disorienting.
  for (const inv of invitations) {
    items.push(invitationPendingFromRow(inv));
  }

  return items;
}

/** Derive an InvitationPendingItem from a pending-for-me row. */
export function invitationPendingFromRow(
  row: InvitationPendingRow,
): InvitationPendingItem {
  return {
    type: "invitation_pending",
    id: `invite:${row.id}`,
    createdAt: row.createdAt,
    invitationId: row.id,
    token: row.token,
    email: row.email,
    orgId: row.orgId,
    orgName: row.orgName,
    role: row.role,
    invitedByName: row.invitedByName,
    expiresAt: row.expiresAt,
  };
}

/**
 * Accept a path-2 invitation from the queue. Hits the existing
 * /invitations/accept endpoint — no separate "queue accept" path.
 * On success, the user's active org list gains the new org; the
 * queue widget refetches to drop the row.
 */
export async function acceptInvitationFromQueue(token: string): Promise<void> {
  await apiFetch(`/invitations/accept`, {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

/** Decline a pending invitation. Server marks declined_at + declined_by;
 *  the item vanishes from pending-for-me on the next refetch. */
export async function declineInvitation(invitationId: string): Promise<void> {
  await apiFetch(`/invitations/${invitationId}/decline`, {
    method: "POST",
  });
}

/**
 * Dismiss the AI split suggestion on a document. Called from the queue
 * when the user says "no thanks" to a split_candidate row — the server
 * strips `metadata.split_suggestion` and the item vanishes on the next
 * listQueueItems() fetch.
 */
export async function dismissSplitSuggestion(documentId: string): Promise<void> {
  await apiFetch(`/documents/${documentId}/split-suggestion/dismiss`, {
    method: "POST",
  });
}

/**
 * Accept or reject an AI-detected calendar event without editing it.
 * For edit-before-accept, users still use the Documents-page EventPanel
 * — the review endpoint accepts the same "edit" action there. The queue
 * flow is deliberately "yes / no" so bulk-accept works predictably.
 */
export async function resolveEventPending(
  documentId: string,
  action: "accept" | "reject",
): Promise<void> {
  await apiFetch(`/documents/${documentId}/event/review`, {
    method: "PATCH",
    body: JSON.stringify({ action }),
  });
}

/** Derive an EventPendingItem from a document_events row. Exported
 *  separately from listQueueItems so the widget can reuse it in
 *  optimistic-append paths without hitting the network. */
export function eventPendingFromRow(row: DocumentEventRow): EventPendingItem {
  return {
    type: "event_pending",
    id: `event:${row.id}`,
    createdAt: row.created_at,
    documentId: row.document_id,
    originalName: row.document_original_name,
    mimeType: row.document_mime_type,
    sizeBytes: row.document_size_bytes,
    eventId: row.id,
    title: row.title,
    startTime: row.start_time,
    endTime: row.end_time,
    allDay: row.all_day,
    location: row.location,
    description: row.description,
  };
}

/**
 * Confirm or reject an AI-suggested filename. On accept, `customName` is
 * what the user actually chose (they may have edited the suggestion). On
 * reject, the server falls back to the original filename.
 *
 * The document's status flips off `pending_review`, so a subsequent
 * listQueueItems() no longer returns this item.
 */
export async function resolveNamePending(
  documentId: string,
  action: "accept" | "reject",
  customName?: string,
): Promise<void> {
  await apiFetch(`/documents/${documentId}/review`, {
    method: "PATCH",
    body: JSON.stringify({
      action,
      ...(action === "accept" && customName ? { customName } : {}),
    }),
  });
}

/** Accept one AI-suggested existing tag — server links the existing tag. */
export async function acceptSuggestedTag(
  documentId: string,
  name: string,
): Promise<void> {
  await apiFetch(`/documents/${documentId}/tags/suggested/accept`, {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

/** Dismiss one AI-suggested existing tag. */
export async function dismissSuggestedTag(
  documentId: string,
  name: string,
): Promise<void> {
  await apiFetch(`/documents/${documentId}/tags/suggested/dismiss`, {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

/** Accept one AI-proposed new tag — server creates the tag then links it. */
export async function acceptProposedTag(
  documentId: string,
  name: string,
): Promise<void> {
  await apiFetch(`/documents/${documentId}/tags/proposed/accept`, {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

/** Dismiss one AI-proposed new tag. */
export async function dismissProposedTag(
  documentId: string,
  name: string,
): Promise<void> {
  await apiFetch(`/documents/${documentId}/tags/proposed/dismiss`, {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

/**
 * Accept every pending tag decision on one document, existing first then
 * proposed. Used by the item-level "Accept all" button and by bulk accept
 * when a tags_pending item is selected.
 *
 * Runs sequentially — the underlying endpoints touch the same document
 * row, and running them in parallel risks last-write-wins races on the
 * document_tags junction.
 */
export async function acceptAllTagsPending(
  item: TagsPendingItem,
): Promise<void> {
  for (const name of item.suggestedTags) {
    await acceptSuggestedTag(item.documentId, name);
  }
  for (const name of item.proposedTags) {
    await acceptProposedTag(item.documentId, name);
  }
}
